import {
  useApolloClient,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  AccountCard,
  AccountTransferStem,
  Alert,
  Button,
  Copyable,
  CurrencyAmountUI,
  cn,
  DataTable,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  entryFromEventFragment,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  LabelPill,
  PrimaryFullScreenSpinner,
  PrimarySpinner,
  Spinner,
  StatusUI,
  TimeLog,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
  toast,
  UserProfile,
  useFXQuote,
} from "@repo/ui";
import {
  download,
  knownProductIds,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink, X } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useEffect, useMemo, useState } from "react";
import {
  PiBuildingOffice,
  PiPlus,
  PiShieldCheck,
  PiWarning,
} from "react-icons/pi";
import { RiExchangeDollarFill } from "react-icons/ri";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { PaneActions, TableAction } from "~/components/operations/actions";
import { FundMovementAddTransaction } from "~/components/operations/FundMovementAddTransaction";
import {
  NewPaymentInstructionFileAction,
  UpdatePaymentInstructionFileActions,
} from "~/components/PaymentInstructionFiles/Actions";
import { TransactionMonitoringFindingSummaryTable } from "~/components/TransactionMonitoring/TransactionMonitoringFindingsSummaryTable";
import { DateDialog as TransferDateDialog } from "~/components/TransferDetail/Details";
import { EditTransferAmountDialog } from "~/components/TransferDetail/EditTransferAmountDialog";
import { graphql } from "~/gql/client";
import type {
  Currency,
  CurrencyAmount,
  FxTradeOperation,
  PaymentInstructionFileKind,
  TransactionQueueTransferFieldsFragment,
  TransactionsQueueTransferPaneFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = [
  "compliance-review",
  "upcoming",
  "ready-today",
  "ready-later",
  "in-progress",
  "recently-completed",
] as const;

type Tab = (typeof tabs)[number];

graphql(`
  fragment TransactionQueueTransferFields on Transfer {
    id
    requestedForDate
    updatedAt
    expectedProcessingDate
    transferStatus: status
    side
    sourceAsset {
      id
      currency
      custodyAccount {
        id
        product {
          id
          internalName
        }
      }
      ... on FixedIncomeFundAsset {
        product {
          id
          internalName
          provider {
            id
            internalName
          }
          ... on FundProduct {
            custodyProviderName
          }
        }
      }
      ... on SavingsAsset {
        product {
          id
          internalName
          provider {
            id
            internalName
          }
        }
      }
      estimatedValue {
        amount
        currency
      }
      availableBalance {
        amount
        currency
      }
      ... on FXAsset {
        provider {
          id
        }
      }
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ...AssetCardFields
    }
    destinationAccount {
      ... on BeneficiaryAccount {
        id
        status
        currency
        ...BeneficiaryAssetCardFields
      }
      ... on Asset {
        id
        currency
        ...AssetCardFields
      }
      ... on FixedIncomeFundAsset {
        product {
          id
          internalName
          provider {
            id
            internalName
          }
          ... on FundProduct {
            custodyProviderName
          }
        }
      }
      ... on SavingsAsset {
        product {
          id
          internalName
          provider {
            id
            internalName
          }
        }
      }
    }
    transferGroup {
      id
      kind
      transfers {
        id
      }
      approvedBy {
        id
        ...UserProfileUI
      }
    }
    currencyAmount {
      amount
      currency
    }
    eligiblePaymentInstructionFileKind
    pendingPaymentInstructionFile {
      id
      shortRef
      kind
      url
    }
    screeningEntity {
      id
      latestValidScreeningResult {
        id
        status
        url
        tags
      }
    }
    ...OperationsTransferActions
  }

  fragment TransactionsQueueTransferPane on Transfer {
    id
    requestedForDate
    description
    updatedAt
    paymentReference
    expectedProcessingDate
    transferStatus: status
    sourceAsset {
      id
      estimatedValue {
        amount
        currency
      }
      availableBalance {
        amount
        currency
      }
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ... on FixedIncomeFundAsset {
        product {
          ... on FundProduct {
            id
            minimumBalance
          }
        }
      }
      ... on SavingsAsset {
        product {
          id
        }
      }
      ...AssetCardFields
    }
    pendingPaymentInstructionFile {
      id
    }
    eligiblePaymentInstructionFileKind
    destinationAccount {
      ... on Asset {
        id
        ...AssetCardFields
      }
      ... on BeneficiaryAccount {
        id
        ...BeneficiaryAssetCardFields
      }
    }
    transferGroup {
      id
      kind
      transfers {
        id
        currencyAmount {
          currency
          amount
        }
        status
        shortRef
        sourceAsset {
          id
          ...AssetCardFields
        }
      }
      approvalRequest {
        id
        approvalPolicy {
          id
          numUserReviewsRequired
        }
        currentApprovedCount
        result
      }
      approvedBy {
        id
        ...UserProfileUI
      }
    }
    currencyAmount {
      amount
      currency
    }
    approvalRequest {
      id
      approvalPolicy {
        id
        numUserReviewsRequired
      }
      currentApprovedCount
      result
    }
    executionMethod
    events {
      ...TimeLogEventEntry
    }
    transactionMonitoringDetails {
      status
      blockingFindings {
        id
        ...TransactionMonitoringFindingSummaryRow
      }
    }
    screeningEntity {
      id
      name
      latestValidScreeningResult {
        id
        status
        url
        tags
        createdAt
      }
    }
    ... @defer {
      ...OperationsTransferActions
    }
    ...AddTransferTransactionDialogFields
    ...EditTransferAmountDialogFields
  }
`);

type Item = TransactionQueueTransferFieldsFragment;

type PerpetualBundleGroup = {
  productId: string;
  productName: string;
  items: Item[];
};

type ItemGroup = {
  title: string;
  items: Item[];
} & (
  | {
      actionKind: "PENDING_FILE";
      fileKind: PaymentInstructionFileKind;
      fileId: string;
      fileUrl: string | undefined;
    }
  | {
      actionKind: "NEW_FILE";
      fileKind: PaymentInstructionFileKind;
    }
  | {
      actionKind: "NONE";
    }
);

const getPaymentInstructionFileTitle = (
  kind: PaymentInstructionFileKind,
): string => {
  switch (kind) {
    case "MACQUARIE_CMA_EGRESS":
      return "Macquarie CMA Egress";
    case "MACQUARIE_CMA_ACCELERATOR_EGRESS":
      return "Macquarie CMA Accelerator to Nominated account";
    case "PERPETUAL_BUNDLE":
      return "Perpetual Bundle";
    case "PERPETUAL_WESTPAC_EGRESS":
      return "Perpetual Westpac Egress";
    default:
      return neverFunc(kind);
  }
};

const getPerpetualBundleGroup = (
  item: TransactionQueueTransferFieldsFragment,
): { productId: string; productName: string } | null => {
  if (
    item.sourceAsset?.__typename === "FixedIncomeFundAsset" &&
    !!item.sourceAsset.product &&
    item.sourceAsset.product?.__typename === "FundProduct" &&
    item.sourceAsset.product.custodyProviderName === "Perpetual"
  ) {
    return {
      productId: item.sourceAsset.product.id,
      productName: item.sourceAsset.product.internalName,
    };
  }

  if (
    item.destinationAccount?.__typename === "FixedIncomeFundAsset" &&
    !!item.destinationAccount.product &&
    item.destinationAccount.product?.__typename === "FundProduct" &&
    item.destinationAccount.product.custodyProviderName === "Perpetual"
  ) {
    return {
      productId: item.destinationAccount.product.id,
      productName: item.destinationAccount.product.internalName,
    };
  }

  return null;
};

const View = () => {
  const params = useParams({ strict: false });

  let selectedTab: Tab | undefined;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionsQueue {
        transactionQueue {
          id
          complianceReview {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
          readyForProcessingLater {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
          readyForProcessingToday {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
          upcoming {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
          inProgress {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
          recentlyCompleted {
            id
            count
            items {
              ...TransactionQueueTransferFields
            }
          }
        }
      }
    `),
  );

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [sidePaneVisible, setSidePaneVisible] = useState<boolean>(false);

  const pendingBeneficiaryUpcomingPayoutCount = useMemo(() => {
    const upcomingItems = data.transactionQueue?.upcoming?.items ?? [];
    return upcomingItems.filter(
      (item) =>
        item.__typename === "Transfer" &&
        item.destinationAccount?.__typename === "BeneficiaryAccount" &&
        item.destinationAccount?.status === "PENDING",
    ).length;
  }, [data.transactionQueue?.upcoming?.items]);

  useEffect(() => {
    if (selectedTab) {
      setSidePaneVisible(false);
    }
  }, [selectedTab]);

  const itemGroups = useMemo(() => {
    let listItems;
    switch (selectedTab) {
      case "compliance-review":
        listItems = data.transactionQueue?.complianceReview?.items ?? [];
        break;
      case "upcoming":
        listItems = data.transactionQueue?.upcoming?.items ?? [];
        break;
      case "ready-today":
        listItems = data.transactionQueue?.readyForProcessingToday?.items ?? [];
        break;
      case "ready-later":
        listItems = data.transactionQueue?.readyForProcessingLater?.items ?? [];
        break;
      case "in-progress":
        listItems = data.transactionQueue?.inProgress?.items ?? [];
        break;
      case "recently-completed":
        listItems = data.transactionQueue?.recentlyCompleted?.items ?? [];
        break;
      default:
        return neverFunc(selectedTab);
    }
    listItems = listItems.filter(
      (li) =>
        li.__typename !== "EburyFXTrade" && li.__typename !== "FundUnitOrder",
    );

    if (selectedTab !== "ready-today") {
      return [
        {
          title: "Transactions",
          items: (listItems ?? []).filter((t) => t.__typename !== "Deposit"),
          actionKind: "NONE",
        },
      ] satisfies ItemGroup[];
    }

    const main: Item[] = [];
    const cmaEgress: Item[] = [];
    const acceleratorEgress: Item[] = [];
    const perpetualBundleByProduct = new Map<string, PerpetualBundleGroup>();
    const perpetualWestpacEgressBySourceCustodyAccountId = new Map<
      string,
      Item[]
    >();
    const pendingFiles = new Map<
      string,
      ItemGroup & { actionKind: "PENDING_FILE" }
    >();

    for (const item of (listItems ?? []).filter(
      (t) => t.__typename !== "Deposit",
    )) {
      if (item.eligiblePaymentInstructionFileKind === "MACQUARIE_CMA_EGRESS") {
        cmaEgress.push(item);
        continue;
      }
      if (
        item.eligiblePaymentInstructionFileKind ===
        "MACQUARIE_CMA_ACCELERATOR_EGRESS"
      ) {
        acceleratorEgress.push(item);
        continue;
      }
      if (item.eligiblePaymentInstructionFileKind === "PERPETUAL_BUNDLE") {
        if (item.__typename !== "Transfer") {
          throw new Error(
            `expected PERPETUAL_BUNDLE item ${item.id} to be a transfer`,
          );
        }

        const perpetualBundleGroup = getPerpetualBundleGroup(item);
        if (!perpetualBundleGroup) {
          throw new Error(
            `expected PERPETUAL_BUNDLE transfer ${item.id} to include a fund product with Perpetual custodyProviderName`,
          );
        }

        const existingGroup = perpetualBundleByProduct.get(
          perpetualBundleGroup.productId,
        );
        if (existingGroup) {
          existingGroup.items.push(item);
        } else {
          perpetualBundleByProduct.set(perpetualBundleGroup.productId, {
            ...perpetualBundleGroup,
            items: [item],
          });
        }
        continue;
      }
      if (
        item.eligiblePaymentInstructionFileKind === "PERPETUAL_WESTPAC_EGRESS"
      ) {
        const sourceCustodyAccount =
          item.sourceAsset &&
          "custodyAccount" in item.sourceAsset &&
          item.sourceAsset?.custodyAccount;
        if (!sourceCustodyAccount) {
          throw new Error(
            `expected PERPETUAL_WESTPAC_EGRESS item ${item.id} to have a source custody account`,
          );
        }

        const existingGroup =
          perpetualWestpacEgressBySourceCustodyAccountId.get(
            sourceCustodyAccount.id,
          );
        if (existingGroup) {
          existingGroup.push(item);
        } else {
          perpetualWestpacEgressBySourceCustodyAccountId.set(
            sourceCustodyAccount.id,
            [item],
          );
        }
        continue;
      }
      if (item.pendingPaymentInstructionFile) {
        let fileGroup = pendingFiles.get(item.pendingPaymentInstructionFile.id);
        if (!fileGroup) {
          fileGroup = {
            title: `Pending Batch file: ${getPaymentInstructionFileTitle(item.pendingPaymentInstructionFile.kind)} #${item.pendingPaymentInstructionFile.shortRef}`,
            actionKind: "PENDING_FILE",
            fileId: item.pendingPaymentInstructionFile.id,
            fileKind: item.pendingPaymentInstructionFile.kind,
            fileUrl: item.pendingPaymentInstructionFile.url ?? undefined,
            items: [],
          };
        }
        fileGroup.items.push(item);
        pendingFiles.set(item.pendingPaymentInstructionFile.id, fileGroup);
        continue;
      }
      main.push(item);
    }

    const itemGroups = (
      [
        ...Array.from(pendingFiles.values()),
        {
          title: "Batch file: Macquarie CMA Egress",
          items: cmaEgress,
          actionKind: "NEW_FILE",
          fileKind: "MACQUARIE_CMA_EGRESS",
        },
        {
          title: "Batch file: Macquarie CMA Accelerator to Nominated account",
          items: acceleratorEgress,
          actionKind: "NEW_FILE",
          fileKind: "MACQUARIE_CMA_ACCELERATOR_EGRESS",
        },
        ...Array.from(perpetualBundleByProduct.values()).map((group) => ({
          title: `Batch file: Perpetual Bundle - ${group.productName}`,
          items: group.items,
          actionKind: "NEW_FILE" as const,
          fileKind: "PERPETUAL_BUNDLE" as const,
        })),
        ...Array.from(
          perpetualWestpacEgressBySourceCustodyAccountId.entries(),
        ).map(([_sourceCustodyAccountId, items]) => {
          const firstItem = items[0];
          const productInternalName =
            firstItem?.sourceAsset &&
            "custodyAccount" in firstItem.sourceAsset &&
            firstItem.sourceAsset.custodyAccount?.product?.internalName
              ? firstItem.sourceAsset.custodyAccount.product.internalName
              : "Unknown product";

          return {
            title: `Batch file: Perpetual Westpac Egress - (${productInternalName})`,
            items,
            actionKind: "NEW_FILE" as const,
            fileKind: "PERPETUAL_WESTPAC_EGRESS" as const,
          };
        }),
        {
          title: "Individual transactions",
          items: main,
          actionKind: "NONE",
        },
      ] satisfies ItemGroup[]
    ).filter((g) => g.items.length > 0);

    if (itemGroups.length === 0) {
      itemGroups.push({
        title: "Transactions",
        items: [],
        actionKind: "NONE",
      });
    }

    return itemGroups;
  }, [selectedTab, data.transactionQueue]);

  const tabColumns = useMemo(() => {
    switch (selectedTab) {
      case "ready-today":
        return columns.filter(
          (c) => !["screening_details", "status"].includes(String(c.id)),
        );
      case "upcoming":
      case "ready-later":
        return columns.filter(
          (c) => !["screening_details", "action"].includes(String(c.id)),
        );
      case "compliance-review":
        return columns.filter(
          (c) =>
            !["status", "funds_available", "action"].includes(String(c.id)),
        );
      case "in-progress":
      case "recently-completed":
        return columns.filter(
          (c) =>
            !["screening_details", "funds_available", "status"].includes(
              String(c.id),
            ),
        );
      default:
        return neverFunc(selectedTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSidePaneVisible(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="m-4">
      <AdminPageHeader title="Transaction Queue" className="ml-4" />
      <div className="flex items-start gap-2">
        <div className="grid w-full flex-1 gap-6" style={{ zIndex: 1 }}>
          <div className="flex justify-start gap-4">
            <OpsTab
              label="Compliance Review"
              badgeNumber={data.transactionQueue?.complianceReview?.count}
              to="compliance-review"
            />
            <OpsTab
              label="Upcoming"
              badgeNumber={data.transactionQueue?.upcoming?.count}
              to="upcoming"
            />
            <OpsTab
              label="Ready today"
              badgeNumber={
                data.transactionQueue?.readyForProcessingToday?.count
              }
              to="ready-today"
            />
            <OpsTab
              label="Ready later"
              badgeNumber={
                data.transactionQueue?.readyForProcessingLater?.count
              }
              to="ready-later"
            />
            <OpsTab
              label="In progress"
              badgeNumber={data.transactionQueue?.inProgress?.count}
              to="in-progress"
            />
            <OpsTab
              label="Recently Completed"
              badgeNumber={data.transactionQueue?.recentlyCompleted?.count}
              to="recently-completed"
            />
          </div>

          {pendingBeneficiaryUpcomingPayoutCount > 0 && (
            <Alert
              kind="warning"
              title="Pending beneficiary account"
              description={`${pendingBeneficiaryUpcomingPayoutCount} upcoming ${
                pendingBeneficiaryUpcomingPayoutCount === 1
                  ? "payout is"
                  : "payouts are"
              } waiting for the beneficiary account to become active before processing.`}
            />
          )}

          {itemGroups.map((group) => {
            return (
              <div
                key={group.title}
                className="flex w-full flex-col gap-6 rounded-xl border bg-primary px-6 pt-6 pb-6 shadow-card"
              >
                <div className="flex justify-between">
                  <Txt variant="section-title">{group.title}</Txt>
                  {group.actionKind === "NEW_FILE" && (
                    <NewPaymentInstructionFileAction
                      kind={group.fileKind}
                      fundMovementIds={group.items.map(({ id }) => id)}
                    />
                  )}
                  {group.actionKind === "PENDING_FILE" && (
                    <UpdatePaymentInstructionFileActions
                      id={group.fileId}
                      url={group.fileUrl}
                    />
                  )}
                </div>

                <DataTable
                  data={group.items}
                  activeRowId={sidePaneVisible ? selectedId : undefined}
                  hidePagination={true}
                  pageSize={10_000}
                  onRowClick={(row) => {
                    setSelectedId(row.original.id);
                    setSidePaneVisible(
                      sidePaneVisible ? row.original.id !== selectedId : true,
                    );
                  }}
                  cellClassName="py-1 px-4"
                  columns={tabColumns}
                />
              </div>
            );
          })}
        </div>

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-1/2 transform overflow-auto border-l bg-primary p-8 shadow-popover duration-300 ease-in-out",
            sidePaneVisible
              ? "translate-x-0"
              : "pointer-events-none translate-x-full",
          )}
          style={{
            zIndex: 1,
          }}
        >
          {selectedId && (
            <FundMovementPane
              onClose={() => setSidePaneVisible(false)}
              fundMovementId={selectedId}
            />
          )}
        </div>
        {sidePaneVisible && (
          <div
            className="absolute top-0 left-0 h-full w-full overflow-hidden"
            style={{
              zIndex: 0,
            }}
            onClick={() => {
              setSidePaneVisible(false);
            }}
          ></div>
        )}
      </div>
    </div>
  );
};

const columns: ColumnDef<TransactionQueueTransferFieldsFragment>[] = [
  {
    header: "Requested",
    cell: ({ row }) => (
      <HoverCard>
        <HoverCardTrigger>
          <DateUI date={row.original.requestedForDate} variant="DAY_MONTH" />
        </HoverCardTrigger>
        <HoverCardContent side="top">
          <DateUI date={row.original.requestedForDate} variant="DATETIME_TZ" />
        </HoverCardContent>
      </HoverCard>
    ),
  },
  {
    header: "Client",
    cell: ({ row }) => {
      const be = row.original.sourceAsset?.businessEntity;

      return (
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <div className="flex min-w-0 max-w-52 flex-row items-center gap-1">
              <Link
                to={`/organisations/${be?.organisation?.id}/business-entities/${be?.id}`}
                className="shrink-0"
              >
                <Icon
                  size="8"
                  imageUrl={be?.organisation?.squareLogoUrl ?? undefined}
                  fallbackIcon={<PiBuildingOffice />}
                />
              </Link>
              <div className="flex min-w-0 flex-col">
                <Txt variant="name" truncate>
                  {be?.name}
                </Txt>
                <Txt variant="caption" truncate>
                  {be?.organisation?.name}
                </Txt>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {be?.organisation?.name} — {be?.name}
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    header: "Approval",
    cell: ({ row }) => (
      <div className="flex flex-row items-center gap-1">
        {row.original.transferGroup?.approvedBy?.map((userProfile) => (
          <UserProfile
            key={userProfile.id}
            variant="avatarOnly"
            {...userProfile}
          />
        ))}
      </div>
    ),
  },
  {
    header: "Group",
    cell: ({ row }) => {
      let label = "";
      switch (row.original.__typename) {
        case "Transfer": {
          if (row.original.transferGroup) {
            label = `${
              (row.original.transferGroup.transfers ?? []).findIndex(
                (t) => t.id === row.original.id,
              ) + 1
            }/${row.original.transferGroup.transfers?.length ?? 0}`;
          } else {
            label = "Transfer";
          }
          break;
        }
      }

      return <LabelPill className="capitalize" size="md" label={label} />;
    },
  },
  {
    header: "Source account",
    cell: ({ row }) =>
      row.original.sourceAsset ? (
        <AccountCard
          subText={["IDENTIFIER", "CURRENCY"]}
          account={row.original.sourceAsset}
        />
      ) : null,
  },
  {
    header: "Destination account",
    cell: ({ row }) => {
      if (
        row.original.__typename === "Transfer" &&
        row.original.destinationAccount
      ) {
        const dest = row.original.destinationAccount;
        if (dest.__typename === "BeneficiaryAccount") {
          return (
            <AccountCard subText={["IDENTIFIER", "CURRENCY"]} account={dest} />
          );
        }
        return (
          <AccountCard subText={["IDENTIFIER", "CURRENCY"]} account={dest} />
        );
      }
      return null;
    },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => {
      const status =
        row.original.__typename === "Transfer"
          ? row.original.transferStatus
          : undefined;

      return (
        status && (
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              <StatusUI status={status} className="flex-shrink" />
            </TooltipTrigger>
            <TooltipContent
              arrowPadding={0}
              className="border-none bg-neutral-solid px-2 py-1 font-text text-sm text-white"
            >
              {DateTime.fromJSDate(row.original.updatedAt).toLocaleString(
                DateTime.DATETIME_MED,
              )}
            </TooltipContent>
          </Tooltip>
        )
      );
    },
  },
  {
    id: "screening_details",
    header: "Screening details",
    cell: ({ row }) => {
      const screeningEntity = row.original.screeningEntity;

      if (!screeningEntity) {
        return <Txt>Screening not required</Txt>;
      }

      const screeningResult = screeningEntity.latestValidScreeningResult;
      if (!screeningResult) {
        return <Txt tone="warning">Screening not found</Txt>;
      }

      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <Txt>{prettyPrintEnum(screeningResult.status)}</Txt>
            {screeningResult.url && (
              <Link
                to={screeningResult.url}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                className="text-tertiary hover:text-primary"
              >
                <ExternalLink className="size-3.5" />
              </Link>
            )}
          </div>
          {screeningResult.tags && screeningResult.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <Txt variant="caption">Flags:</Txt>
              <Txt tone="danger" variant="caption">
                {screeningResult.tags.map((t) => prettyPrintEnum(t)).join(", ")}
              </Txt>
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: "amount",
    header: () => <div className="text-end">Amount</div>,
    cell: ({ row }) => {
      const amount =
        row.original.__typename === "Transfer"
          ? row.original.currencyAmount
          : undefined;
      return (
        amount && (
          <div className="text-end">
            <CurrencyAmountUI
              currency={amount.currency}
              amount={amount.amount}
            />
          </div>
        )
      );
    },
  },
  {
    id: "funds_available",
    header: () => <div className="text-center">Funds available</div>,
    cell: ({ row }) => {
      const sourceAsset = row.original.sourceAsset;
      const sourceAccountCurrentBalance = sourceAsset?.estimatedValue;
      const sourceAccountAvailableBalance = sourceAsset?.availableBalance;
      const destinationCurrency = row.original.destinationAccount?.currency;
      const businessEntityId = sourceAsset?.businessEntity?.id;

      const transferAmount =
        row.original.__typename === "Transfer"
          ? row.original.currencyAmount
          : undefined;

      if (
        sourceAsset &&
        businessEntityId &&
        sourceAccountAvailableBalance &&
        transferAmount &&
        destinationCurrency &&
        sourceAsset.currency !== destinationCurrency
      ) {
        let operation: FxTradeOperation;
        switch (row.original.side) {
          case "SOURCE":
            operation = "SELL";
            break;
          case "DESTINATION":
            operation = "BUY";
            break;
          default:
            return neverFunc(row.original.side);
        }
        const providerId =
          sourceAsset.__typename === "FXAsset"
            ? (sourceAsset.provider?.id ?? null)
            : null;

        return (
          <CrossCurrencyEstimate
            businessEntityId={businessEntityId}
            providerId={providerId}
            operation={operation}
            amount={transferAmount}
            sellCurrency={sourceAsset.currency}
            buyCurrency={destinationCurrency}
            sourceAvailableBalance={sourceAccountAvailableBalance}
            sourceCurrentBalance={sourceAccountCurrentBalance}
          />
        );
      }

      if (
        sourceAccountAvailableBalance &&
        transferAmount &&
        sourceAccountAvailableBalance.currency === transferAmount.currency
      ) {
        let content = <PiWarning className="size-6 text-warning" />;
        if (sourceAccountAvailableBalance.amount.gte(transferAmount.amount)) {
          content = <PiShieldCheck className="size-6 text-success" />;
        }
        return (
          <div className="flex w-full items-center justify-center self-center">
            <Tooltip delayDuration={250}>
              <TooltipTrigger>{content}</TooltipTrigger>
              <TooltipContent
                arrowPadding={0}
                className="border-none bg-neutral-solid px-2 py-1 font-text text-sm text-white"
              >
                <Txt inverseTone>
                  Available:{" "}
                  <CurrencyAmountUI ca={sourceAccountAvailableBalance} />
                </Txt>

                <Txt inverseTone>
                  Current: <CurrencyAmountUI ca={sourceAccountCurrentBalance} />
                </Txt>
              </TooltipContent>
            </Tooltip>
          </div>
        );
      }

      return null;
    },
  },
  {
    id: "action",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return <TableAction {...row.original} />;
    },
  },
];

const CrossCurrencyEstimate = ({
  businessEntityId,
  providerId,
  operation,
  amount,
  sellCurrency,
  buyCurrency,
  sourceAvailableBalance,
  sourceCurrentBalance,
}: {
  businessEntityId: string;
  providerId: string | null;
  operation: FxTradeOperation;
  // In the sell currency for SELL operations, and the buy currency for BUY operations.
  amount: CurrencyAmount;
  sellCurrency: Currency;
  buyCurrency: Currency;
  sourceAvailableBalance: CurrencyAmount;
  sourceCurrentBalance: CurrencyAmount | null | undefined;
}) => {
  const {
    quote,
    loading: quoteLoading,
    errorMessage: quoteError,
  } = useFXQuote({
    quoteKind: "ESTIMATE",
    providerId,
    amount: amount.amount,
    operation,
    sellCurrency,
    buyCurrency,
    businessEntityId,
  });

  let triggerContent;
  let content;
  if (quoteLoading) {
    triggerContent = <Spinner />;
    content = "Loading FX estimate";
  } else if (quote && !quoteError) {
    content = (
      <>
        <Txt inverseTone>
          FX estimate: sell <CurrencyAmountUI ca={quote.sellAmount} />, buy{" "}
          <CurrencyAmountUI ca={quote.buyAmount} />
        </Txt>
        <Txt inverseTone>
          Available: <CurrencyAmountUI ca={sourceAvailableBalance} />
        </Txt>
        <Txt inverseTone>
          Current: <CurrencyAmountUI ca={sourceCurrentBalance} />
        </Txt>
      </>
    );

    if (quote.sellAmount.amount.lte(sourceAvailableBalance.amount)) {
      triggerContent = <PiShieldCheck className="size-6 text-success" />;
    } else {
      triggerContent = <PiWarning className="size-6 text-warning" />;
    }
  } else {
    triggerContent = <PiWarning className="size-6 text-warning" />;
    content = "Failed to get FX estimate";
    if (quoteError) {
      content += ` ${quoteError}`;
    }
  }

  return (
    <div className="flex w-full items-center justify-center self-center">
      <Tooltip delayDuration={250}>
        <TooltipTrigger>{triggerContent}</TooltipTrigger>
        <TooltipContent
          arrowPadding={0}
          className="border-none bg-neutral-solid px-2 py-1"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

function OpsTab({
  to,
  label,
  badgeNumber,
}: {
  to: Tab;
  label: React.ReactNode;
  badgeNumber?: number | null;
}) {
  return (
    <Link
      to={`/transactions-queue/${to}`}
      activeProps={{ className: "active" }}
      className={cn(
        "group grid grid-flow-row place-items-start gap-2 whitespace-nowrap rounded-lg p-4",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "min-w-40 max-w-60",
        "disabled:pointer-events-none disabled:opacity-50",
        "font-normal text-secondary",
        "border border-transparent",
        "[&.active]:bg-secondary [&.active]:font-medium [&.active]:text-primary [&.active]:shadow-popover",
        "hover:bg-neutral-muted hover:shadow-card",
        "cursor-pointer",
      )}
    >
      <span className="text-sm text-tertiary group-[&.active]:text-inherit">
        {label}
      </span>
      {badgeNumber !== undefined && (
        <span className="text-lg">{badgeNumber}</span>
      )}
    </Link>
  );
}

function FundMovementPane({
  fundMovementId,
  onClose,
}: {
  fundMovementId: string;
  onClose: () => void;
}) {
  const { data, loading } = useQuery(
    graphql(`
      query AdminTransactionsQueuePane($id: ID!) {
        node(id: $id) {
          ...TransactionsQueueTransferPane
        }
      }
    `),
    {
      variables: { id: fundMovementId },
      fetchPolicy: "cache-first",
    },
  );
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center opacity-10">
        <PrimarySpinner />
      </div>
    );
  }
  if (!data) {
    throw new Error(`expected ${fundMovementId}`);
  }

  switch (data.node?.__typename) {
    case "Transfer":
      return <TransferPane onClose={onClose} transfer={data.node} />;
    default:
      throw new Error(`unexpected node ${data.node?.__typename}`);
  }
}

function TransferPane({
  transfer,
  onClose,
}: {
  transfer: TransactionsQueueTransferPaneFragment;
  onClose: () => void;
}) {
  const client = useApolloClient();
  const be = transfer.sourceAsset?.businessEntity;

  const isPendingApproval =
    transfer.approvalRequest?.result === "PENDING" ||
    transfer.transferGroup?.approvalRequest?.result === "PENDING";

  const macquarieFileKind = (() => {
    const sourceAsset = transfer.sourceAsset;
    if (
      isPendingApproval ||
      transfer.transferStatus !== "REQUESTED" ||
      transfer.pendingPaymentInstructionFile ||
      transfer.eligiblePaymentInstructionFileKind ||
      sourceAsset?.__typename !== "SavingsAsset"
    ) {
      return null;
    }
    if (sourceAsset.product?.id === knownProductIds.macquarieCma) {
      return "MACQUARIE_CMA_EGRESS" as const;
    }
    if (sourceAsset.product?.id === knownProductIds.macquarieCmaAccelerator) {
      return "MACQUARIE_CMA_ACCELERATOR_EGRESS" as const;
    }
    return null;
  })();

  const [generateMacquarieFile, { loading: generatingMacquarieFile }] =
    useMutation(
      graphql(`
        mutation GenerateMacquariePaymentInstructionFile(
          $input: CreatePaymentInstructionFileInput!
        ) {
          createPaymentInstructionFile(input: $input) {
            paymentInstructionFile {
              id
              url
            }
          }
        }
      `),
      {
        onCompleted(data) {
          const url =
            data.createPaymentInstructionFile?.paymentInstructionFile?.url;
          if (url) {
            download(url);
          }
          toast({
            kind: "success",
            autoHide: true,
            title: "Macquarie payment file generated",
          });
          client.cache.evict({ id: `Transfer:${transfer.id}` });
          client.cache.evict({ fieldName: "transactionQueue" });
          client.cache.gc();
          onClose();
        },
        onError(error) {
          const errorMessage =
            error.graphQLErrors?.[0]?.message ||
            "Failed to generate payment instruction file";
          toast({
            kind: "error",
            autoHide: false,
            title: "Error generating payment instruction file",
            description: errorMessage,
          });
        },
      },
    );

  const sourceMinBalance = (() => {
    if (transfer.sourceAsset?.__typename !== "FixedIncomeFundAsset") {
      return null;
    }
    const product = transfer.sourceAsset.product;
    if (product?.__typename !== "FundProduct") {
      return null;
    }
    return product.minimumBalance ?? null;
  })();

  const wouldBreachMinBalance = (() => {
    if (
      !sourceMinBalance ||
      !transfer.sourceAsset?.availableBalance ||
      !transfer.currencyAmount
    ) {
      return false;
    }
    const remaining = transfer.sourceAsset.availableBalance.amount.sub(
      transfer.currencyAmount.amount,
    );
    return remaining.lte(sourceMinBalance);
  })();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          <div className="flex items-center gap-6">
            <Txt variant="page-title">
              Transfer #{transfer.paymentReference}
            </Txt>

            <Link to={`/transfers/${transfer.id}`} className="hover:underline">
              <Button>View transfer</Button>
            </Link>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="top-2 right-2"
            icon={<X />}
            aria-label="Close"
            onClick={onClose}
          />
        </div>
        <div className="flex flex-row items-center gap-2">
          <div>
            {transfer.transferStatus && (
              <Tooltip delayDuration={250}>
                <TooltipTrigger>
                  <StatusUI
                    status={transfer.transferStatus}
                    className="flex-shrink"
                  />
                </TooltipTrigger>
                <TooltipContent
                  arrowPadding={0}
                  className="border-none bg-neutral-solid px-2 py-1 font-text text-sm text-white"
                >
                  {DateTime.fromJSDate(transfer.updatedAt).toLocaleString(
                    DateTime.DATETIME_MED,
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          ·
          <Link to={`/organisations/${be?.organisation?.id}`}>
            <span className="truncate hover:underline">
              <Txt variant="body">{be?.organisation?.name}</Txt>
            </span>
          </Link>
          ·
          <Link
            to={`/organisations/${be?.organisation?.id}/business-entities/${be?.id}`}
          >
            <span className="truncate hover:underline">
              <Txt variant="body">{be?.name}</Txt>
            </span>
          </Link>
        </div>
      </div>

      <div className="my-4 w-full border-b">
        <Txt variant="card-title">Actions</Txt>
      </div>
      {macquarieFileKind && (
        <Dialog>
          <DialogTrigger asChild>
            <div>
              <Button size="lg">Generate Macquarie payment file</Button>
            </div>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                WARNING: Generate Macquarie payment file
              </DialogTitle>
              <DialogDescription>
                The account balance has not been validated, please ensure that
                funds are available before using this file.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Cancel</Button>
              </DialogClose>
              <DialogClose asChild>
                <Button
                  loading={generatingMacquarieFile}
                  disabled={generatingMacquarieFile}
                  onClick={() => {
                    void generateMacquarieFile({
                      variables: {
                        input: {
                          kind: macquarieFileKind,
                          fundMovementIds: [transfer.id],
                          skipTransferLifecycle: true,
                        },
                      },
                    });
                  }}
                >
                  Generate
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <PaneActions {...transfer} />

      <div className="my-4 flex w-full items-end justify-between border-b">
        <Txt variant="card-title">Details</Txt>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex w-full flex-col justify-between gap-2">
          {transfer.sourceAsset && (
            <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-info bg-info-muted p-4">
              <AccountCard
                subText={["IDENTIFIER", "CURRENCY"]}
                account={transfer.sourceAsset}
                linkTo={`/assets/${transfer.sourceAsset.id}`}
              />
              <div className="flex flex-row items-center gap-8">
                {transfer.sourceAsset?.estimatedValue && (
                  <div className="flex flex-col items-end justify-center">
                    <Txt variant="caption" tone="secondary" nowrap>
                      Current balance
                    </Txt>
                    <CurrencyAmountUI
                      currency={transfer.sourceAsset.estimatedValue.currency}
                      amount={transfer.sourceAsset.estimatedValue.amount}
                    />
                  </div>
                )}
                {transfer.sourceAsset?.availableBalance && (
                  <div className="flex flex-col items-end justify-center">
                    <Txt variant="caption" tone="secondary" nowrap>
                      Available balance
                    </Txt>
                    <CurrencyAmountUI
                      currency={transfer.sourceAsset.availableBalance.currency}
                      amount={transfer.sourceAsset.availableBalance.amount}
                    />
                  </div>
                )}
                <FundMovementAddTransaction
                  createTrigger={
                    <div className="ml-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-primary-inverse hover:border hover:border-grid">
                      <PiPlus />
                    </div>
                  }
                  transactionType="TRANSFER_OUT"
                  {...transfer}
                />
              </div>
            </div>
          )}

          {wouldBreachMinBalance && (
            <Alert
              kind="warning"
              description="State Street accounts require a minimum balance of $15k to remain open. Withdrawing the balance below $15k will result in funds being returned to you and require a new onboarding process to re-open the account. We recommend keeping a minimum of $15k in this account."
            />
          )}

          <AccountTransferStem iconComponent={RiExchangeDollarFill}>
            <div className="flex min-h-16 flex-col justify-center pb-4">
              {transfer.unitAmount && (
                <EditTransferAmountDialog transfer={transfer} />
              )}
            </div>
          </AccountTransferStem>

          {transfer.destinationAccount && (
            <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-info bg-info-muted p-4">
              {transfer.destinationAccount.__typename ===
              "BeneficiaryAccount" ? (
                <AccountCard
                  subText={["IDENTIFIER", "CURRENCY"]}
                  account={transfer.destinationAccount}
                  linkTo={`/beneficiary-accounts/${transfer.destinationAccount.id}`}
                />
              ) : (
                <>
                  <AccountCard
                    subText={["IDENTIFIER", "CURRENCY"]}
                    account={transfer.destinationAccount}
                    linkTo={`/assets/${transfer.destinationAccount.id}`}
                  />
                  <div className="flex flex-row items-center gap-2">
                    {transfer.destinationAccount.estimatedValue && (
                      <div className="flex flex-col items-end justify-center">
                        <Txt variant="caption" tone="secondary" nowrap>
                          Account balance
                        </Txt>
                        <CurrencyAmountUI
                          currency={
                            transfer.destinationAccount.estimatedValue.currency
                          }
                          amount={
                            transfer.destinationAccount.estimatedValue.amount
                          }
                        />
                      </div>
                    )}
                    <FundMovementAddTransaction
                      createTrigger={
                        <div className="ml-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-primary-inverse hover:border hover:border-grid">
                          <PiPlus />
                        </div>
                      }
                      transactionType="TRANSFER_IN"
                      {...transfer}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-row gap-16">
          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Requested at</Txt>
            <Txt variant="value">
              <TransferDateDialog transfer={transfer} />
            </Txt>
          </div>

          {transfer.expectedProcessingDate && (
            <div className="flex flex-col gap-1">
              <Txt variant="detail-label">Processing (est)</Txt>
              <Txt variant="value">
                <DateUI date={transfer.expectedProcessingDate} />
              </Txt>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">Payment reference</Txt>
            <Txt variant="value">
              <Copyable>{transfer.paymentReference}</Copyable>
            </Txt>
          </div>

          {transfer.transferGroup?.approvedBy &&
            transfer.transferGroup.approvedBy.length > 0 && (
              <div className="flex flex-col gap-1">
                <Txt variant="detail-label">Approved by</Txt>
                <div className="flex flex-row items-center gap-1">
                  {transfer.transferGroup.approvedBy.map((userProfile) => (
                    <UserProfile
                      key={userProfile.id}
                      variant="pill"
                      {...userProfile}
                    />
                  ))}
                </div>
              </div>
            )}
        </div>

        <div className="flex flex-col gap-1">
          <Txt variant="detail-label">Description</Txt>
          <Txt variant="value">{transfer.description}</Txt>
        </div>

        {transfer.transferGroup && (
          <div className="flex flex-col gap-1">
            <Txt variant="detail-label">
              Transfer group ({prettyPrintEnum(transfer.transferGroup.kind)})
            </Txt>
            <div className="flex flex-col gap-4">
              <Txt variant="value">
                Transfer #
                {(transfer.transferGroup.transfers ?? []).findIndex(
                  (t) => t.id === transfer.id,
                ) + 1}{" "}
                of {transfer.transferGroup.transfers?.length ?? 0}. Transfer
                group details can be found{" "}
                <Link
                  className="underline"
                  to={`/transfer-groups/${transfer.transferGroup.id}`}
                >
                  here
                </Link>
                .
              </Txt>
            </div>
          </div>
        )}

        {transfer.approvalRequest &&
          transfer.approvalRequest.approvalPolicy &&
          transfer.approvalRequest.approvalPolicy.numUserReviewsRequired >
            0 && (
            <div className="flex flex-col gap-1">
              <Txt variant="detail-label">Approvals</Txt>
              <Txt variant="value">
                Received{" "}
                <strong>{transfer.approvalRequest.currentApprovedCount}</strong>{" "}
                of{" "}
                <strong>
                  {
                    transfer.approvalRequest.approvalPolicy
                      .numUserReviewsRequired
                  }
                </strong>{" "}
                required.
              </Txt>
            </div>
          )}

        {transfer.transferGroup?.approvalRequest &&
          transfer.transferGroup.approvalRequest.approvalPolicy &&
          transfer.transferGroup.approvalRequest.approvalPolicy
            .numUserReviewsRequired > 0 && (
            <div className="flex flex-col gap-1">
              <Txt variant="detail-label">Group approvals</Txt>
              <Txt variant="value">
                Received{" "}
                <strong>
                  {transfer.transferGroup.approvalRequest.currentApprovedCount}
                </strong>{" "}
                of{" "}
                <strong>
                  {
                    transfer.transferGroup.approvalRequest.approvalPolicy
                      .numUserReviewsRequired
                  }
                </strong>{" "}
                required.{" "}
                <StatusUI
                  status={transfer.transferGroup.approvalRequest.result}
                />
              </Txt>
            </div>
          )}

        {transfer.transactionMonitoringDetails && (
          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Transaction Monitoring</Txt>
            {transfer.transactionMonitoringDetails.status && (
              <div>
                <StatusUI
                  status={transfer.transactionMonitoringDetails.status}
                />
              </div>
            )}
            {transfer.transactionMonitoringDetails.blockingFindings &&
            transfer.transactionMonitoringDetails.blockingFindings.length >
              0 ? (
              <>
                <Txt variant="value">
                  This transfer is currently blocked by{" "}
                  {
                    transfer.transactionMonitoringDetails.blockingFindings
                      .length
                  }{" "}
                  findings.
                </Txt>
                <TransactionMonitoringFindingSummaryTable
                  data={transfer.transactionMonitoringDetails.blockingFindings}
                />
              </>
            ) : (
              <Txt variant="value">No blocking findings.</Txt>
            )}
          </div>
        )}

        {transfer.screeningEntity && (
          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Entity screening</Txt>

            <Txt variant="body">
              The business entity "{transfer.screeningEntity.name}" is required
              to be screened before this transfer can continue.
            </Txt>

            <Txt variant="body">
              Screening status{" "}
              {transfer.screeningEntity.latestValidScreeningResult?.status ??
                "Unknown"}{" "}
              {transfer.screeningEntity.latestValidScreeningResult && (
                <Link
                  to={transfer.screeningEntity.latestValidScreeningResult.url}
                >
                  <span className="underline">
                    <Txt variant="body">View screening in BNDRY</Txt>
                  </span>
                </Link>
              )}
            </Txt>

            {transfer.screeningEntity.latestValidScreeningResult?.tags &&
              transfer.screeningEntity.latestValidScreeningResult.tags.length >
                0 && (
                <div className="flex flex-wrap gap-1">
                  <Txt variant="caption">Flags:</Txt>
                  <Txt tone="danger" variant="caption">
                    {transfer.screeningEntity.latestValidScreeningResult.tags
                      .map((t) => prettyPrintEnum(t))
                      .join(", ")}
                  </Txt>
                </div>
              )}
          </div>
        )}

        {transfer.events && (
          <div className="flex flex-col">
            <Txt variant="value">Event log</Txt>
            <TimeLog entries={transfer.events.map(entryFromEventFragment)} />
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/__auth/transactions-queue/$tab")({
  head: () => opsHead("Transactions Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});
